import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { CircularProgress, InputAdornment, IconButton, Typography, OutlinedInput } from '@material-ui/core';
import { Done, Edit } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  editIcon: {
    marginLeft: '8px',
    marginBottom: '2px',
    fontSize: '1.15em',
    color: theme.palette.text.secondary,
    cursor: 'pointer'
  },
  inlineEdit: {
    height: '35px',
    marginLeft: '6px',
    width: '50%',
    paddingRight: '0px'
  },
  errorDiv: {
    paddingTop: '4px',
    color: theme.palette.error.main,
    marginTop: '4px',
    marginLeft: '80px'
  },
  inputDiv: {
    display: 'flex',
    height: '35px',
    alignItems: 'center'
  },
  inputLabel: {
    alignSelf: 'center',
    fontWeight: '500'
  },
  inputValueDiv: {
    paddingLeft: '20px'
  },
  doneIcon: {
    color: theme.palette.success.main
  },
  doneIconDisabled: {
    color: theme.palette.text.disabled
  }
}));

const EditableInput = props => {
  const {
    editableInputName,
    label,
    inputValue,
    updateAction,
    validateForErrors,
    shouldShowError,
    getErrorMessage,
    resetValidationErrors,
    savingInlineUpdate,
    savingInlineSucceeded,
    hideEditIcon
  } = props;

  const classes = useStyles();

  const [modifiedValue, setModifiedValue] = useState({});
  const [showEditInput, setShowEditInput] = useState(false);

  useEffect(() => {
    setModifiedValue(inputValue);
  }, []);

  useEffect(() => {
    if (!savingInlineUpdate && savingInlineSucceeded) {
      setShowEditInput(false);
    }
  }, [savingInlineUpdate, savingInlineSucceeded]);

  const onChangeValue = (rowId, value) => {
    setModifiedValue({ id: rowId, value: value });
    validateForErrors(value);
  };

  const onBlurRowName = () => {
    setModifiedValue(inputValue);
    setShowEditInput(false);
    resetValidationErrors();
  };

  const onClickDoneIcon = () => {
    let errors = validateForErrors(modifiedValue.value, true);
    if (errors.meta.clean) {
      updateAction(modifiedValue.id, modifiedValue.value);
    }
  };

  const getUpdateProgressIcon = () => {
    return savingInlineUpdate ?
      <CircularProgress size={18} /> :
      <Done className={shouldShowError(editableInputName) ? classes.doneIconDisabled : classes.doneIcon} />;
  };

  const getEditableInputComponent = () => {
    return <>
      <OutlinedInput
        className={classes.inlineEdit}
        autoFocus
        size="small"
        value={modifiedValue.id === inputValue.id ? modifiedValue.value : inputValue.value}
        onChange={event => onChangeValue(inputValue.id, event.target.value)}
        onBlur={onBlurRowName}
        error={shouldShowError(editableInputName)}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              data-testid="doneIcon"
              disabled={shouldShowError(editableInputName)}
              onClick={() => onClickDoneIcon()}
              onMouseDown={event => event.preventDefault()}>
              {getUpdateProgressIcon()}
            </IconButton>
          </InputAdornment>
        }
      />
    </>
  };

  return (
    <div>
      <div className={classes.inputDiv}>
        <Typography className={classes.inputLabel}>{label}</Typography>
        {showEditInput ?
          getEditableInputComponent()
          :
          <Typography
            data-testid="inputValue"
            className={classes.inputValueDiv}>
            {inputValue.value}
          </Typography>
        }
        {!showEditInput && !hideEditIcon &&
          <Edit
            data-testid="editIcon"
            className={classes.editIcon}
            onClick={() => setShowEditInput(true)}
          />
        }
      </div>
      {shouldShowError(editableInputName) && (
        <Typography
          data-testid="errorDiv"
          className={classes.errorDiv}
          variant="caption"
          display="block">
          {getErrorMessage(editableInputName)}
        </Typography>
      )}
    </div>
  );
};

EditableInput.propTypes = {
  editableInputName: PropTypes.string,
  label: PropTypes.string,
  inputValue: PropTypes.object,
  updateAction: PropTypes.func,
  validateForErrors: PropTypes.func,
  shouldShowError: PropTypes.func,
  getErrorMessage: PropTypes.func,
  resetValidationErrors: PropTypes.func,
  savingInlineUpdate: PropTypes.bool,
  savingInlineSucceeded: PropTypes.bool,
};

export default EditableInput;